import { useCallback, useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError, currencySymbol } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil, Video, FileSpreadsheet, Download, CheckCheck, CircleX, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { BulkUploadDialog } from "@/components/BulkUploadDialog";
import { MenuItemForm } from "@/components/MenuItemForm";
import { SortableList, DragHandle } from "@/components/SortableList";
import { GroupedItemsDnD } from "@/components/GroupedItemsDnD";
import { ImageCropModal, fileToDataUrl } from "@/components/ImageCropModal";
import { TAG_BY_ID, TagIcon, normalizeDietary } from "@/lib/dietaryTags";
import { API } from "@/lib/api";

function DietTags({ ids }) {
  const list = normalizeDietary(ids);
  if (!list.length) return null;
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {list.map((id) => (
        <span key={id} className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: TAG_BY_ID[id]?.color }}>
          <TagIcon id={id} size={12} /> {TAG_BY_ID[id]?.short}
        </span>
      ))}
    </span>
  );
}

export default function MenuManagement() {
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subs, setSubs] = useState([]);
  const [items, setItems] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [newSub, setNewSub] = useState({ name: "", category_id: "" });
  const [editItem, setEditItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [bulkCatId, setBulkCatId] = useState("");
  const [photoCropSrc, setPhotoCropSrc] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, c, s, i] = await Promise.all([
        api.get("/restaurant"), api.get("/categories"), api.get("/subcategories"), api.get("/items"),
      ]);
      setRestaurant(r.data); setCategories(c.data); setSubs(s.data); setItems(i.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/categories", { name: newCat.trim(), order: categories.length }); setNewCat(""); load(); toast.success("Category added"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delCategory = async (id) => {
    if (!window.confirm("Delete this category and all its items?")) return;
    await api.delete(`/categories/${id}`); load(); toast.success("Category deleted");
  };
  const addSub = async () => {
    if (!newSub.name.trim() || !newSub.category_id) return;
    await api.post("/subcategories", { name: newSub.name.trim(), category_id: newSub.category_id, order: subs.length });
    setNewSub({ name: "", category_id: newSub.category_id }); load(); toast.success("Sub-category added");
  };
  const delSub = async (id) => { await api.delete(`/subcategories/${id}`); load(); };

  // Snapshot the item AT EDIT-OPEN time so we can compute a diff at save-time.
  // Sending only the fields the user actually changed prevents whole-payload
  // PATCHes from accidentally wiping fields like `dietary` whose React state
  // could be stale or affected by a concurrent reload.
  const [editOriginal, setEditOriginal] = useState(null);

  const openNewItem = () => {
    setEditItem({
      name: "", description: "", price: 0,
      category_id: filterCat !== "all" ? filterCat : (categories[0]?.id || ""),
      subcategory_id: null, dietary: [], available: true, in_stock: true, photo: null, video_url: "",
    });
    setEditOriginal(null); // new item → POST the whole thing
    setOpen(true);
  };
  const openEdit = (it) => {
    const normalized = { ...it, dietary: normalizeDietary(it.dietary) };
    setEditItem(normalized);
    setEditOriginal(JSON.parse(JSON.stringify(normalized))); // deep clone for diff
    setOpen(true);
  };

  const saveItem = async () => {
    try {
      if (!editItem.name || !editItem.category_id) return toast.error("Name and category are required");
      if (editItem.id && editOriginal) {
        // PATCH: only send fields that actually changed. Empty-array dietary
        // is only included if the user explicitly cleared a non-empty list.
        const diff = {};
        const fields = ["name", "description", "price", "category_id", "subcategory_id", "dietary", "in_stock", "available", "photo", "video_url"];
        for (const f of fields) {
          const oldV = editOriginal[f];
          let newV = editItem[f];
          if (f === "price") newV = Number(newV) || 0;
          if (f === "subcategory_id" && newV === "none") newV = null;
          if (JSON.stringify(oldV ?? null) !== JSON.stringify(newV ?? null)) diff[f] = newV;
        }
        if (Object.keys(diff).length === 0) {
          setOpen(false); setEditItem(null); setEditOriginal(null);
          return toast.success("No changes");
        }
        await api.patch(`/items/${editItem.id}`, diff);
      } else {
        // Create or fallback path: send full payload
        const payload = { ...editItem, price: Number(editItem.price) || 0 };
        if (payload.subcategory_id === "none") payload.subcategory_id = null;
        await api.post("/items", payload);
      }
      setOpen(false); setEditItem(null); setEditOriginal(null); load(); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/items/${id}`); load();
  };
  const toggleField = async (it, field) => {
    try { await api.patch(`/items/${it.id}`, { [field]: !it[field] }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) return toast.error("Image must be under 6MB");
    // Open the crop modal with the raw image — actual upload happens after crop.
    const url = await fileToDataUrl(f);
    setPhotoCropSrc(url);
  };

  const uploadCroppedPhoto = async (dataUrl) => {
    setPhotoCropSrc(null);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append("file", blob, "photo.jpg");
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditItem((x) => ({ ...x, photo: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sym = currencySymbol(restaurant?.currency || "INR");

  const visibleItems = useMemo(
    () => (filterCat === "all" ? items : items.filter((i) => i.category_id === filterCat)),
    [items, filterCat],
  );

  // Clear any selections that are no longer visible (e.g. after a filter change)
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(visibleItems.map((i) => i.id));
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleItems]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    const visibleIds = visibleItems.map((i) => i.id);
    const allSelected = visibleIds.every((id) => selected.has(id));
    setSelected(() => (allSelected ? new Set() : new Set(visibleIds)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected item${selected.size === 1 ? "" : "s"}? This can't be undone.`)) return;
    try {
      const { data } = await api.post("/items/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted} item${data.deleted === 1 ? "" : "s"} deleted`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const bulkUpdate = async (update) => {
    if (selected.size === 0) return;
    try {
      const { data } = await api.post("/items/bulk-update", { ids: [...selected], ...update });
      toast.success(`${data.updated} item${data.updated === 1 ? "" : "s"} updated`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportMenu = async () => {
    try {
      const resp = await fetch(`${API}/items/export`, { credentials: "include" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `menumojo-${restaurant?.slug || "menu"}-export.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Menu exported");
    } catch (e) {
      toast.error("Export failed. Please try again.");
    }
  };

  // --- Drag-and-drop reorder handlers ----------------------------------
  // Optimistic: update UI instantly, then persist. On failure, reload from
  // the server so the UI snaps back to the true order.
  const reorderCategories = async (newIds) => {
    const byId = new Map(categories.map((c) => [c.id, c]));
    setCategories(newIds.map((id) => byId.get(id)).filter(Boolean));
    try {
      await api.post("/categories/reorder", { ids: newIds });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const reorderSubcategories = async (catId, newIdsWithinCat) => {
    // Rebuild the full list: for the affected category replace its subs in
    // the new order, others keep their relative position. Backend stores a
    // single `order` per sub-category regardless of category; flat re-index
    // over the whole list keeps GET /subcategories in a sensible order.
    const affected = new Set(newIdsWithinCat);
    const rebuilt = [];
    const insertPoint = subs.findIndex((s) => affected.has(s.id));
    subs.forEach((s, idx) => {
      if (affected.has(s.id)) return;
      if (idx === insertPoint) return;
      rebuilt.push(s);
    });
    const byId = new Map(subs.map((s) => [s.id, s]));
    const orderedForCat = newIdsWithinCat.map((id) => byId.get(id)).filter(Boolean);
    const finalList = [
      ...rebuilt.slice(0, insertPoint === -1 ? rebuilt.length : insertPoint),
      ...orderedForCat,
      ...rebuilt.slice(insertPoint === -1 ? rebuilt.length : insertPoint),
    ];
    setSubs(finalList);
    try {
      await api.post("/subcategories/reorder", { ids: finalList.map((s) => s.id) });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  // Called by <GroupedItemsDnD/> after a successful drop (same-group reorder
  // OR cross-group move). Does all the plumbing:
  //   1. Optimistic local update (move item + reorder).
  //   2. If category changed → PATCH /items/:id with new category/subcategory.
  //   3. POST /items/reorder with the full list so `order` is consistent.
  const moveItem = async ({ id, category_id, subcategory_id, orderedIdsInTargetGroup, sourceGroupKey, targetGroupKey }) => {
    const moving = items.find((i) => i.id === id);
    if (!moving) return;
    const categoryChanged = sourceGroupKey !== targetGroupKey;

    // Optimistic local state: update the moved item's category + rebuild full
    // item array so its position matches the new target-group ordering.
    const updated = items.map((i) =>
      i.id === id ? { ...i, category_id, subcategory_id: subcategory_id || null } : i,
    );
    // Reorder the target group within the full list: keep non-target items
    // in place, then splice in the target group's new order at the position
    // of its first member.
    const targetSet = new Set(orderedIdsInTargetGroup);
    const others = updated.filter((i) => !targetSet.has(i.id));
    const targetOrdered = orderedIdsInTargetGroup
      .map((tid) => updated.find((i) => i.id === tid))
      .filter(Boolean);
    const anchor = updated.findIndex((i) => targetSet.has(i.id));
    const merged =
      anchor === -1
        ? [...others, ...targetOrdered]
        : [...others.slice(0, anchor), ...targetOrdered, ...others.slice(anchor)];
    setItems(merged);

    try {
      if (categoryChanged) {
        await api.patch(`/items/${id}`, { category_id, subcategory_id: subcategory_id || null });
      }
      await api.post("/items/reorder", { ids: merged.map((i) => i.id) });
      if (categoryChanged) {
        const catName = categories.find((c) => c.id === category_id)?.name || "";
        const subName = subcategory_id ? subs.find((s) => s.id === subcategory_id)?.name : null;
        toast.success(`Moved to ${catName}${subName ? " · " + subName : ""}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <DashboardLayout role="owner">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">Menu</div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Manage your menu</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={exportMenu} variant="outline" className="rounded-full" data-testid="export-menu-btn">
            <Download className="w-4 h-4" /> Export
          </Button>
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="rounded-full" data-testid="bulk-upload-btn">
            <FileSpreadsheet className="w-4 h-4" /> Bulk import
          </Button>
          <Button onClick={openNewItem} className="bg-brand-orange hover:bg-[#E65C00] text-white rounded-full" data-testid="add-item-button">
            <Plus className="w-4 h-4" /> Add item
          </Button>
        </div>
      </div>

      <BulkUploadDialog open={bulkOpen} onOpenChange={setBulkOpen} onSuccess={load} />

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Card className="p-6 border border-border">
          <h3 className="font-heading font-semibold mb-3">Categories</h3>
          <div className="flex gap-2 mb-4">
            <Input placeholder="Eg. Starters" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="new-category-input" />
            <Button onClick={addCategory} className="bg-brand-navy hover:bg-[#1A2B42] text-white" data-testid="add-category-btn">Add</Button>
          </div>
          <div className="space-y-2">
            <SortableList
              items={categories}
              onReorder={reorderCategories}
              dataTestId="categories-sortable"
              renderItem={(c, { dragHandleProps, isDragging }) => (
                <div
                  className={`flex items-center gap-2 bg-muted px-3 py-2 rounded-md text-sm mb-2 ${isDragging ? "ring-1 ring-brand-orange/50" : ""}`}
                  data-testid={`category-row-${c.id}`}
                >
                  <DragHandle dragHandleProps={dragHandleProps} label={`Drag ${c.name}`} />
                  <span className="font-medium flex-1">{c.name}</span>
                  <button
                    type="button"
                    onClick={() => delCategory(c.id)}
                    className="text-destructive hover:opacity-80"
                    data-testid={`del-cat-${c.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            />
            {categories.length === 0 && <div className="text-sm text-muted-foreground">No categories yet.</div>}
          </div>
        </Card>

        <Card className="p-6 border border-border lg:col-span-2">
          <h3 className="font-heading font-semibold mb-3">Sub-categories</h3>
          <div className="grid sm:grid-cols-3 gap-2 mb-4">
            <Select value={newSub.category_id} onValueChange={(v) => setNewSub({ ...newSub, category_id: v })}>
              <SelectTrigger data-testid="new-sub-category-select"><SelectValue placeholder="Pick a category" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Eg. Hot, Cold" value={newSub.name} onChange={(e) => setNewSub({ ...newSub, name: e.target.value })} data-testid="new-sub-name-input" />
            <Button onClick={addSub} className="bg-brand-navy hover:bg-[#1A2B42] text-white" data-testid="add-sub-btn">Add sub-category</Button>
          </div>
          <div className="space-y-3">
            {categories.map((c) => {
              const subsInCat = subs.filter((s) => s.category_id === c.id);
              if (subsInCat.length === 0) return null;
              return (
                <div key={c.id} data-testid={`subcat-group-${c.id}`}>
                  <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-1.5">{c.name}</div>
                  <SortableList
                    items={subsInCat}
                    onReorder={(newIds) => reorderSubcategories(c.id, newIds)}
                    strategy="horizontal"
                    dataTestId={`subcats-sortable-${c.id}`}
                    renderItem={(s, { dragHandleProps, isDragging }) => (
                      <span
                        className={`inline-flex items-center gap-1.5 bg-muted rounded-full pl-2 pr-1 py-1 text-xs font-semibold mr-2 mb-2 ${isDragging ? "ring-1 ring-brand-orange/50" : ""}`}
                        data-testid={`subcat-chip-${s.id}`}
                      >
                        <DragHandle dragHandleProps={dragHandleProps} label={`Drag ${s.name}`} className="mx-0 px-0" />
                        <span>{s.name}</span>
                        <button
                          type="button"
                          onClick={() => delSub(s.id)}
                          className="text-destructive hover:opacity-80 ml-1"
                          data-testid={`del-sub-${s.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  />
                </div>
              );
            })}
            {subs.length === 0 && <div className="text-sm text-muted-foreground">No sub-categories yet.</div>}
          </div>
        </Card>
      </div>

      <Card className="border border-border">
        <div className="p-6 border-b border-border flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <h3 className="font-heading font-semibold text-lg">
            Menu items {filterCat === "all" ? `(${items.length})` : `· ${categories.find((c) => c.id === filterCat)?.name || ""}`}
          </h3>
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Filter</Label>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-[220px]" data-testid="filter-category-select">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories ({items.length})</SelectItem>
                {categories.map((c) => {
                  const n = items.filter((i) => i.category_id === c.id).length;
                  return <SelectItem key={c.id} value={c.id} data-testid={`filter-cat-${c.id}`}>{c.name} ({n})</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="divide-y">
          {visibleItems.length > 0 && (
            <div className="px-5 py-3 flex items-center gap-3 bg-muted/40 text-sm">
              <Checkbox
                checked={visibleItems.length > 0 && visibleItems.every((i) => selected.has(i.id))}
                onCheckedChange={toggleAllVisible}
                data-testid="select-all-checkbox"
                aria-label="Select all visible items"
              />
              <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold">
                {selected.size > 0 ? `${selected.size} selected` : "Select all"}
              </span>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="ml-auto text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  data-testid="clear-selection"
                >
                  <XIcon className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
          )}
          {items.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">No items yet. Create your first one!</div>}
          {items.length > 0 && (
            <div className="p-4">
              <GroupedItemsDnD
                items={items}
                categories={categories}
                subs={subs}
                filterCategoryId={filterCat}
                onMoveItem={moveItem}
                renderGroupHeader={(g) => (
                  <div className="flex items-baseline gap-2 px-2 py-2 border-b border-border/60">
                    <span className="font-heading font-semibold text-sm">{g.category.name}</span>
                    {g.subcategory && (
                      <>
                        <span className="text-xs text-muted-foreground">›</span>
                        <span className="text-sm text-muted-foreground">{g.subcategory.name}</span>
                      </>
                    )}
                    <span className="ml-auto text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-bold">
                      {g.items.length} item{g.items.length === 1 ? "" : "s"}
                    </span>
                  </div>
                )}
                renderEmptyGroup={(g) => (
                  <div
                    className="px-4 py-4 text-xs text-muted-foreground italic border border-dashed border-border/60 rounded-md m-2"
                    data-testid={`empty-group-${g.key}`}
                  >
                    Drop items here
                  </div>
                )}
                renderItem={(it, g, { dragHandleProps, isDragging }) => {
                  const isSel = selected.has(it.id);
                  return (
                    <div
                      className={`border-b last:border-b-0 p-4 flex flex-col md:flex-row md:items-center gap-4 transition-colors ${isSel ? "bg-brand-orange/5" : ""} ${isDragging ? "bg-muted/60" : ""}`}
                      data-testid={`item-row-${it.id}`}
                    >
                      <DragHandle dragHandleProps={dragHandleProps} label={`Drag ${it.name}`} />
                      <Checkbox
                        checked={isSel}
                        onCheckedChange={() => toggleOne(it.id)}
                        aria-label={`Select ${it.name}`}
                        data-testid={`select-${it.id}`}
                      />
                      <div className="w-14 h-14 rounded-lg bg-muted overflow-hidden shrink-0">
                        {it.photo ? <img src={it.photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">No img</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-semibold">{it.name}</span>
                          <DietTags ids={it.dietary} />
                          {it.video_url && <Video className="w-3.5 h-3.5 text-muted-foreground" />}
                        </div>
                        {it.description && <div className="text-sm text-foreground/70 mt-1 line-clamp-1">{it.description}</div>}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="font-heading font-bold brand-orange">{sym}{Number(it.price).toFixed(2)}</div>
                        <div className="flex items-center gap-2">
                          <Switch checked={it.in_stock} onCheckedChange={() => toggleField(it, "in_stock")} data-testid={`toggle-stock-${it.id}`} />
                          <span className="text-xs hidden sm:inline">{it.in_stock ? "Available" : "OOS"}</span>
                        </div>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => delItem(it.id)} data-testid={`del-item-${it.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          )}
          {items.length > 0 && filterCat !== "all" && visibleItems.length === 0 && (
            <div className="p-10 text-center text-muted-foreground text-sm" data-testid="no-items-in-cat">No items in this category yet.</div>
          )}
        </div>
      </Card>

      {/* Sticky bulk-action bar */}
      {selected.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-brand-navy text-white rounded-full shadow-2xl px-3 py-2 flex items-center gap-2 flex-wrap"
          data-testid="bulk-action-bar"
        >
          <span className="text-xs font-bold uppercase tracking-[0.15em] px-3">
            {selected.size} selected
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/10 rounded-full h-8"
            onClick={() => bulkUpdate({ in_stock: true })}
            data-testid="bulk-mark-available"
          >
            <CheckCheck className="w-4 h-4" /> Available
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/10 rounded-full h-8"
            onClick={() => bulkUpdate({ in_stock: false })}
            data-testid="bulk-mark-oos"
          >
            <CircleX className="w-4 h-4" /> Out of stock
          </Button>
          <div className="h-6 w-px bg-white/20 mx-1" />
          <Select value={bulkCatId} onValueChange={(v) => { setBulkCatId(v); bulkUpdate({ category_id: v }); setBulkCatId(""); }}>
            <SelectTrigger className="bg-transparent border-white/20 hover:bg-white/10 text-white h-8 rounded-full text-xs w-[170px]" data-testid="bulk-move-category">
              <SelectValue placeholder="Move to category…" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="h-6 w-px bg-white/20 mx-1" />
          <Button
            size="sm"
            className="bg-destructive hover:bg-destructive/90 text-white rounded-full h-8"
            onClick={bulkDelete}
            data-testid="bulk-delete"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </Button>
        </div>
      )}

      <MenuItemForm
        open={open}
        onOpenChange={setOpen}
        editItem={editItem}
        setEditItem={setEditItem}
        categories={categories}
        subs={subs}
        sym={sym}
        onSave={saveItem}
        onPhoto={onPhoto}
      />

      <ImageCropModal
        open={!!photoCropSrc}
        src={photoCropSrc}
        onClose={() => setPhotoCropSrc(null)}
        onConfirm={uploadCroppedPhoto}
        aspect={1}
        cropShape="rect"
        title="Crop dish photo"
        description="Pan and zoom so the dish fits the thumbnail square."
      />
    </DashboardLayout>
  );
}
